import { useState, useCallback, useEffect, useRef } from 'react';
import { useComponentVisibility } from '@/hooks/useComponentVisibility';
import type { OrganizerEventResponse } from '../types';
import type { BasicInfo } from '@/types';

const isElementVisible = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  return rect.top < windowHeight && rect.bottom > 0 && rect.left < windowWidth && rect.right > 0;
};

const EMPTY_RESPONSE: OrganizerEventResponse = { events: [], unrecoverableEventIds: [] };

const useFetchOrganizerEvents = (
  organizerId: string | undefined,
  eventId: string,
  initialData?: OrganizerEventResponse,
) => {
  const [organizerEvents, setOrganizerEvents] = useState<OrganizerEventResponse | null>(initialData ?? null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchEvents = useCallback(async () => {
    if (!organizerId) return;

    setIsLoading(true);
    try {
      const fetchUrl = `/e/api/${eventId}/more-organizer-events?organizerId=${encodeURIComponent(organizerId)}`;
      const response = await fetch(fetchUrl);

      if (!response.ok) {
        console.error('Failed to fetch organizer events:', response.status, response.statusText);
        setOrganizerEvents(EMPTY_RESPONSE);
        return;
      }

      const payload = (await response.json()) as OrganizerEventResponse;
      setOrganizerEvents(payload && Array.isArray(payload.events) ? payload : EMPTY_RESPONSE);
    } catch (err) {
      console.error('Failed to fetch organizer events:', err);
      setOrganizerEvents(EMPTY_RESPONSE);
    } finally {
      setIsLoading(false);
    }
  }, [organizerId, eventId]);

  return { organizerEvents, isLoading, fetchEvents };
};

// Extract scroll handling logic to reduce complexity
const useScrollHandler = (isBodyRendered: boolean, checkIfInViewAndLoad: () => void) => {
  useEffect(() => {
    if (!isBodyRendered) return;

    let scrollTimer: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(checkIfInViewAndLoad, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimer);
    };
  }, [isBodyRendered, checkIfInViewAndLoad]);
};

export const useMoreOrganizerEventsLazy = (
  eventBasicInfo: BasicInfo,
  eventId: string,
  isBodyRendered?: boolean,
  initialData?: OrganizerEventResponse,
) => {
  const organizerId = eventBasicInfo.organizer?.id;
  const { organizerEvents, isLoading, fetchEvents } = useFetchOrganizerEvents(organizerId, eventId, initialData);

  // Use refs to avoid infinite loops caused by state dependencies
  const isLoadingRef = useRef(isLoading);
  const organizerEventsRef = useRef(organizerEvents);

  // Keep refs in sync with state
  isLoadingRef.current = isLoading;
  organizerEventsRef.current = organizerEvents;

  const fetchIfNeeded = useCallback(async () => {
    if (organizerId && !isLoadingRef.current && !organizerEventsRef.current) {
      await fetchEvents();
    }
  }, [fetchEvents, organizerId]);

  const elementRef = useComponentVisibility(fetchIfNeeded, {
    rootMargin: '50px',
  });

  const checkIfInViewAndLoad = useCallback(() => {
    const element = elementRef.current;
    const shouldFetch = !!(
      element &&
      !isLoadingRef.current &&
      !organizerEventsRef.current &&
      isElementVisible(element)
    );
    if (shouldFetch) {
      fetchEvents();
    }
  }, [fetchEvents, elementRef]);

  // Check on body render
  useEffect(() => {
    if (isBodyRendered) {
      checkIfInViewAndLoad();
    }
  }, [isBodyRendered, checkIfInViewAndLoad]);

  // Handle scroll events
  useScrollHandler(isBodyRendered || false, checkIfInViewAndLoad);

  return {
    organizerEvents,
    isLoading,
    elementRef,
  };
};
